// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import {
  addDrug,
  deleteDrug,
  updateDrug,
  archiveDrug,
  recoverDrug,
} from '../../actions/pharmacyActions';

import DrugDispenser from './DrugDispenser.jsx';
import AddDrugModal from './AddDrugModal.jsx';
import Table from '../Table.jsx';
import Modal from '../Modal.jsx';
import { Button, PrimaryButton, DeleteButton } from '../Buttons.jsx';
import { Error, Warning } from '../Typography.jsx';

type Props = {
  drugList: Object,
  category: string,
  addDrug: Function,
  deleteDrug: Function,
  updateDrug: Function,
  archiveDrug: Function,
  recoverDrug: Function,
  activeAccount: Object,
  pharmacyFilter?: string,
}

type State = {
  modalOpen: boolean,
  addModalOpen: boolean,
  viewOnly: boolean,
  editRow: ?Object,
}

class DrugTableComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      modalOpen: false,
      addModalOpen: false,
      viewOnly: false,
      editRow: null,
    };
  }

  // Modal state handlers. editRow is set to row object if editing, else null.
  handleEditRow = (row: Object) => {
    this.setState({
      modalOpen: true,
      viewOnly: false,
      editRow: row,
    });
  }

  handleViewRow = (row: Object) => {
    this.setState({
      modalOpen: true,
      viewOnly: true,
      editRow: row,
    });
  }

  handleAddRow = () => {
    this.setState({
      addModalOpen: true,
      editRow: null,
    });
  }

  closeModal = () => {
    this.setState({
      modalOpen: false,
      editRow: null,
    });
  }

  closeAddModal = () => {
    this.setState({
      addModalOpen: false,
      editRow: null,
    });
  }

  /* eslint-disable no-param-reassign */
  transformDates(formValues: Object) {
    formValues.manufactureDate = moment(formValues.manufactureDate, 'DD-MM-YYYY').toISOString();
    formValues.expirationDate = moment(formValues.expirationDate, 'DD-MM-YYYY').toISOString();
    formValues.currDate = moment(formValues.currDate, 'DD-MM-YYYY, HH:mm').toISOString();
  }

  handleArchiveRow = (id: string) => {
    const { activeAccount } = this.props;
    this.props.archiveDrug(id, `${activeAccount.name}, ${activeAccount.credential}`);
  }

  handleDeleteRow = (id: string) => {
    const { activeAccount } = this.props;
    this.props.deleteDrug(id, `${activeAccount.name}, ${activeAccount.credential}`);
  }

  handleRecoverRow = (id: string) => {
    const { activeAccount } = this.props;
    this.props.recoverDrug(id, `${activeAccount.name}, ${activeAccount.credential}`);
  }

  handleSaveRow = (id: string, formData: Object) => {
    this.transformDates(formData.values);
    // Add active account to requests for edit history
    const { activeAccount } = this.props;
    this.props.updateDrug({
      drugId: id,
      editor: `${activeAccount.name}, ${activeAccount.credential}`,
      editDate: moment().toISOString(),
      ...formData.values,
    });
  }

  handleSaveNewRow = (formData: Object) => {
    this.transformDates(formData.values);
    // Add active account to requests for edit history
    const { category, activeAccount } = this.props;
    this.props.addDrug({
      category,
      editor: `${activeAccount.name}, ${activeAccount.credential}`,
      editDate: moment().toISOString(),
      ...formData.values,
    });
  }

  // Create action column for each row
  actionsAccessor = (row: Object) => {
    const defaultButtons = (
      <div>
        <Button onClick={() => this.handleViewRow(row)}>View</Button>
        <PrimaryButton onClick={() => this.handleEditRow(row)}>Edit</PrimaryButton>
        <DeleteButton archive onClick={() => this.handleArchiveRow(row._id)}>Archive</DeleteButton>
      </div>
    );
    return defaultButtons;
  }

  archiveActionsAccessor = (row: Object) => {
    const defaultButtons = (
      <div>
        <Button onClick={() => this.handleViewRow(row)}>View</Button>
        <PrimaryButton onClick={() => this.handleRecoverRow(row._id)}>Recover</PrimaryButton>
        <DeleteButton onClick={() => this.handleDeleteRow(row._id)}>Delete</DeleteButton>
      </div>
    );
    return defaultButtons;
  }

  markErrorDate = (drug: Object) => {
    // expired if expiration date past today
    const now = moment().startOf('day').toISOString();
    return (drug.status === 0)
      && drug.expirationDate
      && drug.expirationDate < now;
  }

  markErrorQuantity = (drug: Object) => (drug.status === 0) && (drug.numberRemainingUnits <= 0);

  markWarningDate = (drug: Object) => {
    // expiring if expiration date in next 30 days
    const expiringDeadline = moment().add(30, 'day').startOf('day').toISOString();
    return (drug.status === 0)
      && drug.expirationDate
      && drug.expirationDate < expiringDeadline;
  }

  markWarningQuantity = (drug: Object) => {
    // low stock if less than 10 units
    const LOW_STOCK_THRESHOLD = 10;
    return (drug.status === 0) && (drug.numberRemainingUnits > 0)
      && (drug.numberRemainingUnits < LOW_STOCK_THRESHOLD);
  }

  filterDrugs = (drugList: Object) => {
    let filteredDrugs = drugList[this.props.category] || [];
    /* Name changed from Asthma/Breathing treatments -> Respiratory meds, so concat both for this category */
    if (this.props.category === 'Respiratory Meds') {
      filteredDrugs = (drugList['Asthma/Breathing treatments'] || []).concat(filteredDrugs);
    }
    let { pharmacyFilter } = this.props;
    if (!pharmacyFilter) {
      return filteredDrugs;
    }
    pharmacyFilter = pharmacyFilter.toLowerCase();
    return filteredDrugs.filter(drug => (drug.proprietaryName && drug.proprietaryName.toLowerCase().includes(pharmacyFilter))
      || (drug.genericName && drug.genericName.toLowerCase().includes(pharmacyFilter))
      || (drug.type && drug.type.toLowerCase().includes(pharmacyFilter)));
  }

  render() {
    const { category, drugList } = this.props;

    const filteredDrugs = this.filterDrugs(drugList);

    const columns = [
      {
        Header: 'Generic Name',
        id: 'genericName',
        cell: (item) => {
          const value = item.genericName ? item.genericName : '--';
          if (this.markErrorDate(item) || this.markErrorQuantity(item)) {
            return <Error>{value}</Error>;
          }
          if (this.markWarningDate(item) || this.markWarningQuantity(item)) {
            return <Warning>{value}</Warning>;
          }
          return value;
        },
      },
      {
        Header: 'Proprietary Name',
        id: 'proprietaryName',
        cell: item => item.proprietaryName || '--',
      },
      {
        Header: 'Concentration',
        id: 'concentration',
        cell: item => item.concentration || '--',
      },
      {
        Header: 'Drug Type',
        id: 'type',
        cell: item => item.type || '--',
      },
      {
        Header: 'Amt/Unit',
        id: 'amountPerUnit',
        cell: item => item.amountPerUnit || '--',
      },
      {
        Header: 'Expiration Date',
        id: 'expirationDate',
        cell: (item) => {
          if (!item.expirationDate) {
            return '--';
          }
          const value = moment(item.expirationDate).format();
          if (this.markErrorDate(item)) {
            return <Error>{value}</Error>;
          }
          if (this.markWarningDate(item)) {
            return <Warning>{value}</Warning>;
          }
          return value;
        },
      },
      {
        Header: '# Units Remaining',
        id: 'numberRemainingUnits',
        cell: (item) => {
          const value = item.numberRemainingUnits;
          if (!this.markErrorQuantity) {
            return <Error>0</Error>;
          }
          if (this.markErrorQuantity(item)) {
            return <Error>{value}</Error>;
          }
          if (this.markWarningQuantity(item)) {
            return <Warning>{value}</Warning>;
          }
          return value;
        },
      },
    ];

    return (
      <div>
        {category
          && <PrimaryButton onClick={this.handleAddRow}>Add Row</PrimaryButton>
        }
        <Table
          columns={columns}
          data={filteredDrugs}
          extraActions={item => (item.status === 0 ? this.actionsAccessor(item) : this.archiveActionsAccessor(item))}
          actionsWidth={175}
        />
        <Modal isOpen={this.state.modalOpen} style={{ minWidth: '60%' }}>
          <DrugDispenser
            viewOnly={this.state.viewOnly}
            drug={this.state.editRow || {}}
            handleSaveRow={this.handleSaveRow}
            handleSaveNewRow={this.handleSaveNewRow}
            closeModal={this.closeModal}
          />
        </Modal>
        <Modal isOpen={this.state.addModalOpen}>
          <AddDrugModal
            handleSaveNewRow={this.handleSaveNewRow}
            closeModal={this.closeAddModal}
            category={category || ''}
          />
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  drugList: state.pharmacy.data.drugList,
  pharmacyFilter: state.pharmacy.data.pharmacyFilter,
  activeAccount: state.account.activeAccount,
});

const mapDispatchToProps = dispatch => ({
  addDrug: drug => dispatch(addDrug(drug)),
  deleteDrug: (drugId, editor) => dispatch(deleteDrug(drugId, editor)),
  updateDrug: drug => dispatch(updateDrug(drug)),
  archiveDrug: (drugId, editor) => dispatch(archiveDrug(drugId, editor)),
  recoverDrug: (drugId, editor) => dispatch(recoverDrug(drugId, editor)),
});

const DrugTable = connect(
  mapStateToProps,
  mapDispatchToProps,
)(DrugTableComponent);

export default DrugTable;



// WEBPACK FOOTER //
// ./client/src/components/pharmacyPages/DrugTable.jsx